import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import * as Sentry from '@sentry/react'
import { setContractReporter } from './contracts'

// ── NOCHE 2 · A2 · CONSENTIMIENTO ANTES QUE TELEMETRÍA (ePrivacy + dato de salud) ──
// F7 verificó: Sentry (con replay) y posthog.identify corrían INCONDICIONALES antes de
// ningún consentimiento, y el banner de cookies jamás se montaba. Regla: el registro de
// ERRORES mínimo es interés legítimo (integridad del servicio); replay/tracing/analytics
// son opcionales y solo arrancan con consentimiento 'analytics' explícito.
function hasAnalyticsConsent() {
  try {
    const raw = window.localStorage.getItem('pl_cookie_consent')
    if (!raw) return false
    const parsed = JSON.parse(raw)
    return parsed?.analytics === true
  } catch (_) { return false }
}
const _analyticsConsented = hasAnalyticsConsent()

// ── Sentry — Sprint 14c A7 ───────────────────────────────────────────────────
// DSN via REACT_APP_SENTRY_DSN env var. No-op in development unless explicitly set.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || 'production',
    release: process.env.REACT_APP_VERSION || 'primelab@alpha',
    // A2 · replay/tracing SOLO con consentimiento analytics; sin él, errores desnudos
    integrations: _analyticsConsented ? [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Record only on error (not full session) — privacy-first
        maskAllText: true,
        blockAllMedia: true,
      }),
    ] : [],
    // Performance sampling — minimal for alpha (0 sin consentimiento)
    tracesSampleRate: _analyticsConsented ? 0.1 : 0,
    // Replay only on errors
    replaysOnErrorSampleRate: _analyticsConsented ? 1.0 : 0,
    replaysSessionSampleRate: 0,
    // Don't capture localhost errors
    beforeSend(event) {
      if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SENTRY_FORCE_DEV) {
        return null // drop in dev unless forced
      }
      return event
    },
  })
}

// ── MACRO 1 · Catarsis 1: violaciones de contrato → Sentry ───────────────────
// Cada contrato roto entre capas (motor↔API↔UI) llega aquí con el shape
// real vs esperado. Fail-loud: trazable, nunca enmascarado por fallback.
setContractReporter((message, context) => {
  Sentry.captureMessage(message, {
    level: 'error',
    tags: { kind: 'contract_violation', contract: context?.contract },
    extra: context,
  })
})

// A2 · PostHog solo con key Y consentimiento analytics (antes: solo la key)
// LAZY (opt: bundle) · posthog-js (~47KB gzip) sale del chunk main y solo se DESCARGA
// aquí, en el momento del consentimiento. Sin consentimiento el chunk nunca se pide.
// El singleton cargado se expone en window.__posthog para que App.js/authStore.js lo
// usen sin re-importar el paquete (mismos call sites, ya guardados con .__loaded).
if (process.env.REACT_APP_POSTHOG_KEY && _analyticsConsented) {
  import('posthog-js').then(({ default: posthog }) => {
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: 'https://eu.i.posthog.com',
      capture_pageview: true,
      autocapture: false,
    })
    window.__posthog = posthog
  }).catch(() => {})
}

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(<App />)

// ── Service Worker — Sprint 19 ────────────────────────────────────────────────
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then(registration => {
        console.log('[SW] Registered:', registration.scope)

        // Notify app when a new SW is waiting (triggers update banner)
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version is ready — dispatch event for UpdateBanner component
              window.dispatchEvent(new CustomEvent('sw-update-ready', { detail: { registration } }))
            }
          })
        })
      })
      .catch(err => console.error('[SW] Registration failed:', err))

    // COLA:la-pestana-de-ayer-se-rompe-tras-la-promocion · se recuerda la versión que esta pestaña cargó (el `main.js` con hash del manifiesto del build):
// es con lo que `cargaDeTrozo` compara antes de pedir cualquier trozo diferido. Si no se puede leer, no se bloquea nada.
require('./lib/cargaDeTrozo').recordarVersion()

// Reload once after SW takes control (for first-install)
    let refreshing = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) { refreshing = true; window.location.reload() }
    })
  })
}
